// Équipements de mesure et étalonnage (ISO 9001 §7.1.5) : presse à éprouvettes, machine de flexion, balances…
// Chaque équipement a une périodicité d'étalonnage ; chaque étalonnage est tracé (date, organisme, résultat, certificat).
import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Alert, Box, Button, Chip, Dialog, DialogActions, DialogContent, DialogTitle, Grid, IconButton, MenuItem, Table, TableBody,
  TableCell, TableContainer, TableHead, TableRow, TextField, Tooltip, Typography,
} from '@mui/material';
import {
  AddOutlined, AttachFileOutlined, DeleteOutline, EditOutlined, OpenInNewOutlined, ScienceOutlined, StraightenOutlined,
} from '@mui/icons-material';
import { Panel } from '../../components/ui';
import { qualiteAPI } from '../../services/api';
import { apiError, dateFr, today } from '../../utils/format';

const TYPES = { PRESSE: 'Presse à éprouvettes', FLEXION: 'Machine de flexion', BALANCE: 'Balance', THERMOMETRE: 'Thermomètre', METRE: 'Mètre / pied à coulisse', AUTRE: 'Autre' };
const RESULTATS = { CONFORME: ['Conforme', 'success'], AJUSTE: ['Ajusté', 'info'], NON_CONFORME: ['Non conforme', 'error'] };
const STATUTS = { EN_SERVICE: ['En service', 'success'], HORS_SERVICE: ['Hors service', 'warning'], REFORME: ['Réformé', 'default'] };
const EMPTY = { nom: '', type: 'PRESSE', code: '', numero_serie: '', localisation: '', periodicite_jours: '365', organisme: '', statut: 'EN_SERVICE', observation: '' };

function EquipementDialog({ ouvert, equip, onClose, onDone }) {
  const [f, setF] = useState(EMPTY);
  const [error, setError] = useState('');
  useEffect(() => { if (ouvert) { setError(''); setF(equip ? { ...EMPTY, ...equip, periodicite_jours: String(equip.periodicite_jours) } : EMPTY); } }, [ouvert, equip]);
  const set = (k) => (e) => setF((x) => ({ ...x, [k]: e.target.value }));
  const save = async () => {
    setError('');
    try { const r = equip ? await qualiteAPI.modifierEquipement(equip.id, f) : await qualiteAPI.creerEquipement(f); onDone(r.data.message); } catch (e) { setError(apiError(e)); }
  };
  if (!ouvert) return null;
  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>{equip ? 'Modifier l\'équipement' : 'Nouvel équipement de mesure'}</DialogTitle>
      <DialogContent dividers>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        <Grid container spacing={2}>
          <Grid item xs={12} sm={8}><TextField fullWidth label="Nom *" value={f.nom} onChange={set('nom')} /></Grid>
          <Grid item xs={12} sm={4}><TextField fullWidth select label="Type" value={f.type} onChange={set('type')}>{Object.entries(TYPES).map(([k, l]) => <MenuItem key={k} value={k}>{l}</MenuItem>)}</TextField></Grid>
          <Grid item xs={6} sm={4}><TextField fullWidth label="Code / repère" value={f.code} onChange={set('code')} /></Grid>
          <Grid item xs={6} sm={4}><TextField fullWidth label="N° de série" value={f.numero_serie} onChange={set('numero_serie')} /></Grid>
          <Grid item xs={12} sm={4}><TextField fullWidth label="Localisation" value={f.localisation} onChange={set('localisation')} /></Grid>
          <Grid item xs={6} sm={4}><TextField fullWidth label="Périodicité (jours)" value={f.periodicite_jours} onChange={set('periodicite_jours')} inputProps={{ inputMode: 'numeric' }} helperText="365 = tous les ans" /></Grid>
          <Grid item xs={6} sm={4}><TextField fullWidth select label="Statut" value={f.statut} onChange={set('statut')}>{Object.entries(STATUTS).map(([k, l]) => <MenuItem key={k} value={k}>{l[0]}</MenuItem>)}</TextField></Grid>
          <Grid item xs={12} sm={4}><TextField fullWidth label="Organisme d'étalonnage" value={f.organisme} onChange={set('organisme')} /></Grid>
          <Grid item xs={12}><TextField fullWidth label="Observation" value={f.observation} onChange={set('observation')} /></Grid>
        </Grid>
      </DialogContent>
      <DialogActions><Button onClick={onClose}>Annuler</Button><Button variant="contained" disabled={!f.nom.trim()} onClick={save}>Enregistrer</Button></DialogActions>
    </Dialog>
  );
}

function DetailDialog({ id, peut, onClose, onInfo }) {
  const [d, setD] = useState(null);
  const [error, setError] = useState('');
  const [et, setEt] = useState(null); // formulaire nouvel étalonnage
  const input = useRef();
  const certPour = useRef(null);
  const charger = useCallback(() => { if (id) qualiteAPI.equipement(id).then((r) => setD(r.data)).catch((e) => setError(apiError(e))); }, [id]);
  useEffect(() => { charger(); }, [charger]);
  const enregistrerEt = async () => {
    setError('');
    try { const r = await qualiteAPI.ajouterEtalonnage(id, et); setEt(null); onInfo(r.data.message); charger(); } catch (e) { setError(apiError(e)); }
  };
  const supprimerEt = async (eid) => { if (!window.confirm('Supprimer cet étalonnage ?')) return; try { await qualiteAPI.supprimerEtalonnage(eid); charger(); } catch (e) { setError(apiError(e)); } };
  const voirCert = async (eid) => { try { const r = await qualiteAPI.certificat(eid); const u = URL.createObjectURL(r.data); window.open(u, '_blank', 'noopener'); setTimeout(() => URL.revokeObjectURL(u), 60000); } catch (e) { setError(apiError(e)); } };
  const choisirCert = async (e) => {
    const file = e.target.files?.[0]; e.target.value = ''; if (!file || !certPour.current) return;
    try { await qualiteAPI.joindreCertificat(certPour.current, file); charger(); } catch (x) { setError(apiError(x)); }
  };
  if (!id) return null;
  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle>{d ? d.nom : 'Équipement'}</DialogTitle>
      <DialogContent dividers>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        {d && (
          <>
            <Typography sx={{ fontSize: 13.5, color: 'text.secondary', mb: 2 }}>
              {[TYPES[d.type], d.code, d.numero_serie && `n° ${d.numero_serie}`, d.localisation, `tous les ${d.periodicite_jours} j`, d.organisme].filter(Boolean).join(' · ')}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2, flexWrap: 'wrap' }}>
              <Chip size="small" color={STATUTS[d.statut][1]} label={STATUTS[d.statut][0]} />
              <Typography sx={{ fontSize: 14 }}>Dernier : {d.dernier_etalonnage ? dateFr(d.dernier_etalonnage) : '—'} · Prochain : {d.prochain_etalonnage ? dateFr(d.prochain_etalonnage) : '—'}</Typography>
              {d.en_retard && <Chip size="small" color="error" label="Étalonnage en retard" />}
              {!d.en_retard && d.a_prevoir && <Chip size="small" color="warning" label="À prévoir" />}
              <Box sx={{ flex: 1 }} />
              {peut && <Button size="small" variant="contained" startIcon={<AddOutlined />} onClick={() => setEt({ date: today(), organisme: d.organisme || '', resultat: 'CONFORME', prochain: '', note: '' })}>Nouvel étalonnage</Button>}
            </Box>
            <Table size="small">
              <TableHead><TableRow><TableCell>Date</TableCell><TableCell>Organisme</TableCell><TableCell>Résultat</TableCell><TableCell>Prochain</TableCell><TableCell>Certificat</TableCell>{peut && <TableCell />}</TableRow></TableHead>
              <TableBody>
                {d.etalonnages.map((t) => (
                  <TableRow key={t.id} hover>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>{dateFr(t.date)}</TableCell>
                    <TableCell sx={{ fontSize: 13 }}>{t.organisme || '—'}{t.note && <Typography sx={{ fontSize: 11.5, color: 'text.secondary' }}>{t.note}</Typography>}</TableCell>
                    <TableCell><Chip size="small" color={RESULTATS[t.resultat][1]} label={RESULTATS[t.resultat][0]} /></TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>{t.prochain ? dateFr(t.prochain) : '—'}</TableCell>
                    <TableCell>
                      {t.cert_nom
                        ? <Button size="small" startIcon={<OpenInNewOutlined />} onClick={() => voirCert(t.id)}>Voir</Button>
                        : peut && <Button size="small" startIcon={<AttachFileOutlined />} onClick={() => { certPour.current = t.id; input.current?.click(); }}>Joindre</Button>}
                    </TableCell>
                    {peut && <TableCell align="right"><Tooltip title="Supprimer"><IconButton size="small" onClick={() => supprimerEt(t.id)}><DeleteOutline fontSize="small" /></IconButton></Tooltip></TableCell>}
                  </TableRow>
                ))}
                {!d.etalonnages.length && <TableRow><TableCell colSpan={peut ? 6 : 5} sx={{ py: 2, textAlign: 'center', color: 'text.secondary' }}>Aucun étalonnage enregistré.</TableCell></TableRow>}
              </TableBody>
            </Table>
            <input ref={input} type="file" hidden accept="image/*,application/pdf" onChange={choisirCert} aria-label="Certificat" />
          </>
        )}
      </DialogContent>
      <DialogActions><Button onClick={onClose}>Fermer</Button></DialogActions>

      <Dialog open={!!et} onClose={() => setEt(null)} fullWidth maxWidth="xs">
        <DialogTitle>Nouvel étalonnage</DialogTitle>
        <DialogContent dividers>
          {et && (
            <Grid container spacing={2} sx={{ mt: 0 }}>
              <Grid item xs={6}><TextField fullWidth type="date" label="Date *" InputLabelProps={{ shrink: true }} value={et.date} onChange={(e) => setEt({ ...et, date: e.target.value })} /></Grid>
              <Grid item xs={6}><TextField fullWidth type="date" label="Prochain (sinon auto)" InputLabelProps={{ shrink: true }} value={et.prochain} onChange={(e) => setEt({ ...et, prochain: e.target.value })} /></Grid>
              <Grid item xs={12}><TextField fullWidth label="Organisme" value={et.organisme} onChange={(e) => setEt({ ...et, organisme: e.target.value })} /></Grid>
              <Grid item xs={12}><TextField fullWidth select label="Résultat" value={et.resultat} onChange={(e) => setEt({ ...et, resultat: e.target.value })}>{Object.entries(RESULTATS).map(([k, l]) => <MenuItem key={k} value={k}>{l[0]}</MenuItem>)}</TextField></Grid>
              <Grid item xs={12}><TextField fullWidth multiline label="Note" value={et.note} onChange={(e) => setEt({ ...et, note: e.target.value })} /></Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions><Button onClick={() => setEt(null)}>Annuler</Button><Button variant="contained" onClick={enregistrerEt}>Enregistrer</Button></DialogActions>
      </Dialog>
    </Dialog>
  );
}

const Equipements = ({ resume, onInfo, refresh }) => {
  const peut = resume?.responsable;
  const [liste, setListe] = useState(null);
  const [error, setError] = useState('');
  const [dlg, setDlg] = useState(null);   // { equip } ou {}
  const [detail, setDetail] = useState(null);
  const charger = useCallback(() => qualiteAPI.equipements().then((r) => setListe(r.data)).catch((e) => setError(apiError(e))), []);
  useEffect(() => { charger(); }, [charger]);
  const supprimer = async (e) => { if (!window.confirm(`Supprimer « ${e.nom} » et son historique d'étalonnage ?`)) return; try { await qualiteAPI.supprimerEquipement(e.id); charger(); refresh(); } catch (x) { setError(apiError(x)); } };

  return (
    <>
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
      <Panel flush>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 1.5, borderBottom: '1px solid', borderColor: 'divider' }}>
          <StraightenOutlined color="action" />
          <Typography sx={{ fontWeight: 600, flex: 1 }}>Équipements de mesure</Typography>
          {peut && <Button variant="contained" startIcon={<AddOutlined />} onClick={() => setDlg({})}>Nouvel équipement</Button>}
        </Box>
        <TableContainer>
          <Table size="small">
            <TableHead><TableRow>
              <TableCell>Équipement</TableCell><TableCell>Localisation</TableCell><TableCell>Dernier étalonnage</TableCell>
              <TableCell>Prochain</TableCell><TableCell>Statut</TableCell><TableCell /></TableRow></TableHead>
            <TableBody>
              {liste?.map((e) => (
                <TableRow key={e.id} hover sx={{ cursor: 'pointer', bgcolor: e.en_retard ? '#FCEDEE' : undefined }} onClick={() => setDetail(e.id)}>
                  <TableCell>
                    <Typography sx={{ fontWeight: 600, fontSize: 14 }}>{e.nom}</Typography>
                    <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>{[TYPES[e.type], e.code, e.numero_serie && `n° ${e.numero_serie}`].filter(Boolean).join(' · ')}</Typography>
                  </TableCell>
                  <TableCell sx={{ fontSize: 13 }}>{e.localisation || '—'}</TableCell>
                  <TableCell sx={{ fontSize: 13, whiteSpace: 'nowrap' }}>{e.dernier_etalonnage ? dateFr(e.dernier_etalonnage) : '—'}</TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>
                    {e.prochain_etalonnage ? dateFr(e.prochain_etalonnage) : '—'}
                    {e.en_retard && <Chip size="small" color="error" label="en retard" sx={{ ml: 0.5, height: 18 }} />}
                    {!e.en_retard && e.a_prevoir && e.statut === 'EN_SERVICE' && <Chip size="small" color="warning" label="à prévoir" sx={{ ml: 0.5, height: 18 }} />}
                  </TableCell>
                  <TableCell><Chip size="small" color={STATUTS[e.statut][1]} variant="outlined" label={STATUTS[e.statut][0]} /></TableCell>
                  <TableCell align="right" onClick={(ev) => ev.stopPropagation()}>
                    {peut && <>
                      <Tooltip title="Modifier"><IconButton size="small" onClick={() => setDlg({ equip: e })}><EditOutlined fontSize="small" /></IconButton></Tooltip>
                      <Tooltip title="Supprimer"><IconButton size="small" onClick={() => supprimer(e)}><DeleteOutline fontSize="small" /></IconButton></Tooltip>
                    </>}
                  </TableCell>
                </TableRow>
              ))}
              {liste && !liste.length && <TableRow><TableCell colSpan={6} sx={{ py: 3, textAlign: 'center', color: 'text.secondary' }}>Aucun équipement. Ajoutez la presse à éprouvettes, la machine de flexion…</TableCell></TableRow>}
            </TableBody>
          </Table>
        </TableContainer>
      </Panel>
      <EquipementDialog ouvert={!!dlg} equip={dlg?.equip} onClose={() => setDlg(null)} onDone={(m) => { setDlg(null); onInfo(m); charger(); refresh(); }} />
      <DetailDialog id={detail} peut={peut} onClose={() => { setDetail(null); charger(); refresh(); }} onInfo={onInfo} />
    </>
  );
};

export default Equipements;
